import React, { useState } from "react";
import { Box, TextField, Button, Typography, InputAdornment, Link, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Email as EmailIcon, Lock as LockIcon } from "@mui/icons-material";
import { apiFetch } from "./apiFetch";
import { USE_MOCK_DATA, mockUser } from "../utils/mockData";
import PreAuthShell from "./PreAuthShell";

const SignInPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  const LOGIN_URL = "https://refinery-user-service-hvg0g8ceddefaseb.australiasoutheast-01.azurewebsites.net/login";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (USE_MOCK_DATA) {
      localStorage.setItem("authToken", mockUser.token);
      navigate("/newnote");
      return;
    }
    try {
      const response = await apiFetch(LOGIN_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("authToken", data.token);
        navigate("/app");
        // Redirect to the dashboard or another page
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Login failed. Please try again.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("An error occurred. Please try again.");
    }
  };

  return (
    <PreAuthShell
      title="Welcome back"
      subtitle="Sign in to continue building your knowledge graph."
      footer={
        <Typography variant="body2" align="center" sx={{ color: "#5a4a7b" }}>
          Don&apos;t have an account?{" "}
          <Link
            component={RouterLink}
            to="/register"
            sx={{ color: "#492099", textDecoration: "underline", fontWeight: 600 }}
          >
            Register
          </Link>
        </Typography>
      }
    >
      {error && (
        <Typography color="error" sx={{ marginBottom: 2, textAlign: "center" }}>
          {error}
        </Typography>
      )}

      <Box component="form" onSubmit={handleLogin} sx={{ width: "100%" }}>
        <TextField
          label="Email"
          name="email"
          type="email"
          variant="outlined"
          fullWidth
          margin="normal"
          value={form.email}
          onChange={handleChange}
          required
          InputProps={{
            sx: { "& input": { fontSize: 16 } },
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon sx={{ color: "#8b7fa3" }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Password"
          name="password"
          type="password"
          variant="outlined"
          fullWidth
          margin="normal"
          value={form.password}
          onChange={handleChange}
          required
          InputProps={{
            sx: { "& input": { fontSize: 16 } },
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon sx={{ color: "#8b7fa3" }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
          <Link
            component="button"
            type="button"
            onClick={() => setForgotPasswordOpen(true)}
            sx={{ color: "#492099", textDecoration: "underline", fontSize: 13, fontWeight: 600 }}
          >
            Forgot password?
          </Link>
        </Box>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{
            mt: 2,
            mb: 0.5,
            py: 1.35,
            borderRadius: 99,
            fontSize: 16,
            fontWeight: 600,
            textTransform: "none",
            background: "linear-gradient(135deg, #D7C4FA 0%, #8C60E6 45%, #4B1FC5 100%)",
            boxShadow: "0 10px 20px rgba(90, 50, 186, 0.3)",
            ":hover": {
              boxShadow: "0 12px 22px rgba(90, 50, 186, 0.35)",
            },
          }}
        >
          Log in
        </Button>
      </Box>
      <Dialog open={forgotPasswordOpen} onClose={() => setForgotPasswordOpen(false)}>
        <DialogTitle>Forgot password</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#4b3c67", mt: 0.5 }}>
            If you forgot your password please email noterefinery@littleoxfordstreet.com
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setForgotPasswordOpen(false)} sx={{ textTransform: "none", fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </PreAuthShell>
  );
};

export default SignInPage;
